// ── Event types ──────────────────────────────────────────────────────────────

export type EventType =
  | 'reuniao'
  | 'evento'
  | 'atividade'
  | 'panfletagem'
  | 'outro';

export type EventStatus =
  | 'scheduled'
  | 'ongoing'
  | 'completed'
  | 'cancelled';

export type AttendeeStatus =
  | 'invited'
  | 'confirmed'
  | 'declined'
  | 'attended'
  | 'no_show';

export type EventColor =
  | 'cyan'
  | 'blue'
  | 'emerald'
  | 'amber'
  | 'rose'
  | 'purple';

// ── Domain models ─────────────────────────────────────────────────────────────

export interface Event {
  id: string;
  tenant_id: string;
  title: string;
  description: string | null;
  type: EventType;
  status: EventStatus;
  location_name: string | null;
  location_address: string | null;
  starts_at: string;
  ends_at: string | null;
  notes: string | null;
  color: EventColor;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface EventAttendee {
  id: string;
  event_id: string;
  contact_id: string;
  status: AttendeeStatus;
  notes: string | null;
  invited_at: string;
  responded_at: string | null;
  // joined from contacts
  contact?: {
    id: string;
    full_name: string;
    phone: string | null;
    category: string | null;
  };
}

// ── Form values ────────────────────────────────────────────────────────────────

export interface EventFormValues {
  title: string;
  description: string;
  type: EventType;
  status: EventStatus;
  location_name: string;
  location_address: string;
  starts_at: string;
  ends_at: string;
  notes: string;
  color: EventColor;
}

// ── Display helpers ────────────────────────────────────────────────────────────

export const EVENT_TYPE_LABELS: Record<EventType, string> = {
  reuniao:     'Reunião',
  evento:      'Evento',
  atividade:   'Atividade',
  panfletagem: 'Panfletagem',
  outro:       'Outro',
};

export const EVENT_STATUS_LABELS: Record<EventStatus, string> = {
  scheduled: 'Agendado',
  ongoing:   'Em andamento',
  completed: 'Concluído',
  cancelled: 'Cancelado',
};

export const EVENT_STATUS_COLORS: Record<EventStatus, string> = {
  scheduled: 'bg-blue-500/15 text-blue-300',
  ongoing:   'bg-amber-500/15 text-amber-300',
  completed: 'bg-emerald-500/15 text-emerald-300',
  cancelled: 'bg-danger/15 text-danger',
};

export const ATTENDEE_STATUS_LABELS: Record<AttendeeStatus, string> = {
  invited:   'Convidado',
  confirmed: 'Confirmado',
  declined:  'Recusou',
  attended:  'Compareceu',
  no_show:   'Ausente',
};

export const ATTENDEE_STATUS_COLORS: Record<AttendeeStatus, string> = {
  invited:   'bg-slate-500/15 text-slate-300',
  confirmed: 'bg-emerald-500/15 text-emerald-300',
  declined:  'bg-danger/15 text-danger',
  attended:  'bg-blue-500/15 text-blue-300',
  no_show:   'bg-amber-500/15 text-amber-300',
};

// Color slug → Tailwind/CSS classes for calendar dots + cards
export const EVENT_COLOR_MAP: Record<EventColor, { dot: string; card: string; text: string }> = {
  cyan:    { dot: 'bg-cyan-400',    card: 'border-l-cyan-400',    text: 'text-cyan-300'    },
  blue:    { dot: 'bg-blue-400',    card: 'border-l-blue-400',    text: 'text-blue-300'    },
  emerald: { dot: 'bg-emerald-400', card: 'border-l-emerald-400', text: 'text-emerald-300' },
  amber:   { dot: 'bg-amber-400',   card: 'border-l-amber-400',   text: 'text-amber-300'   },
  rose:    { dot: 'bg-rose-400',    card: 'border-l-rose-400',    text: 'text-rose-300'    },
  purple:  { dot: 'bg-purple-400',  card: 'border-l-purple-400',  text: 'text-purple-300'  },
};
